import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';
import { Field, inputClass } from '../../../../components/ui/Field';
import { AddressFields, validateAddress } from '../../../../components/address/AddressFields';
import { COUNTRY_ISO2 } from '../../../../features/geo/geoConstants';
import { selectCountryIso2 } from '../../../../features/auth/authSelectors';
import {
  createPickupPoint,
  updatePickupPoint,
} from '../../../../features/pickupPoints/pickupPointsThunks';
import {
  BLANK_PICKUP_POINT,
  formFromPickupPoint,
  payloadFromForm,
  validatePickupPoint,
} from '../../../../features/pickupPoints/pickupPointsUtils';

const TimeRange = ({ label, from, to, onChange, error, disabled }) => (
  <Field label={label} error={error}>
    <div className="flex items-center gap-2">
      <input
        type="time"
        className={inputClass}
        value={from.value}
        onChange={(e) => onChange(from.key, e.target.value)}
        disabled={disabled}
        aria-label={`${label} desde`}
      />
      <span className="text-text-muted text-sm">a</span>
      <input
        type="time"
        className={inputClass}
        value={to.value}
        onChange={(e) => onChange(to.key, e.target.value)}
        disabled={disabled}
        aria-label={`${label} hasta`}
      />
    </div>
  </Field>
);

const PickupPointForm = ({ point, onClose }) => {
  const dispatch = useDispatch();
  const countryIso2 = useSelector(selectCountryIso2) || COUNTRY_ISO2.CL;
  const countryId = useSelector((state) => state.auth.user?.admin?.country_fk?.id || null);
  const isMexico = countryIso2 === COUNTRY_ISO2.MX;

  // Seeded once, at mount: the dialog is remounted on every opening.
  const [form, setForm] = useState(() => (point ? formFromPickupPoint(point) : BLANK_PICKUP_POINT));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  /** `AddressFields` sends only what changed, cascade included. */
  const handleAddressChange = (next) => {
    setForm((prev) => ({ ...prev, ...next }));
    setErrors((prev) => {
      const cleared = { ...prev };
      Object.keys(next).forEach((key) => { cleared[key] = undefined; });
      if ('settlement' in next) cleared.settlementId = undefined;
      return cleared;
    });
  };

  const validate = () => {
    const found = { ...validateAddress(form, { isMexico }), ...validatePickupPoint(form) };
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setError(null);
    const payload = payloadFromForm(form, { countryId });
    const action = point
      ? await dispatch(updatePickupPoint({ pickupPointId: point.id, payload }))
      : await dispatch(createPickupPoint(payload));
    setSaving(false);
    if (action.meta.requestStatus === 'fulfilled') onClose();
    else setError(action.payload);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={point ? `Editar punto de retiro` : 'Nuevo punto de retiro'}
      size="lg"
      busy={saving}
      error={error}
      errorTitle="No se pudo guardar"
      testId="pickup-point-form"
    >
      <div className="space-y-5">
        <Field label="Nombre" htmlFor="pickup-point-name" error={errors.name}>
          <input
            id="pickup-point-name"
            className={inputClass}
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Ej: Providencia"
          />
        </Field>

        <AddressFields
          country={countryIso2}
          value={form}
          onChange={handleAddressChange}
          errors={errors}
        />

        <Field label="Referencias del local" htmlFor="pickup-point-comments">
          <input
            id="pickup-point-comments"
            className={inputClass}
            value={form.comments}
            onChange={(e) => setField('comments', e.target.value)}
            placeholder="Portón azul, segundo piso..."
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Encargado" htmlFor="pickup-point-keeper">
            <input
              id="pickup-point-keeper"
              className={inputClass}
              value={form.storekeeperName}
              onChange={(e) => setField('storekeeperName', e.target.value)}
            />
          </Field>
          <Field label="Teléfono" htmlFor="pickup-point-phone">
            <input
              id="pickup-point-phone"
              className={inputClass}
              value={form.storekeeperPhone}
              onChange={(e) => setField('storekeeperPhone', e.target.value)}
            />
          </Field>
          <Field label="Email" htmlFor="pickup-point-email">
            <input
              id="pickup-point-email"
              type="email"
              className={inputClass}
              value={form.storekeeperEmail}
              onChange={(e) => setField('storekeeperEmail', e.target.value)}
            />
          </Field>
        </div>

        <div className="space-y-3">
          <span className="block text-sm font-semibold text-ninja-dark dark:text-white">
            Horario de recepción
          </span>
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={form.allDay}
              onChange={(e) => setField('allDay', e.target.checked)}
              className="accent-ninja-green"
            />
            Todo el día
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TimeRange
              label="Mañana"
              from={{ key: 'amFrom', value: form.amFrom }}
              to={{ key: 'amTo', value: form.amTo }}
              onChange={setField}
              error={errors.amFrom || errors.amTo}
              disabled={form.allDay}
            />
            <TimeRange
              label="Tarde"
              from={{ key: 'pmFrom', value: form.pmFrom }}
              to={{ key: 'pmTo', value: form.pmTo }}
              onChange={setField}
              error={errors.pmFrom || errors.pmTo}
              disabled={form.allDay}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="subtle" pill onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            pill
            onClick={handleSave}
            loading={saving}
            loadingLabel="Guardando..."
          >
            {point ? 'Guardar cambios' : 'Crear punto de retiro'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Creates or edits a pickup point: a name, an address from the official
 * catalog, who to call there and when it receives parcels.
 *
 * Mounted only while open, so each opening starts from the point as it is.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} [props.point] - The point to edit; none creates one.
 */
export const PickupPointFormModal = ({ isOpen, onClose, point = null }) =>
  isOpen ? <PickupPointForm point={point} onClose={onClose} /> : null;
